import {
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  memo,
} from 'react';
import Observer from '@researchgate/react-intersection-observer';
import { useFlag } from '@simplywallst/unleash';
import {
  Container,
  CompanyReportWrapper,
  SectionWrapper,
  FixedWrapper,
  Logo,
  PrintModeWatermark,
  WatermarkCopy,
} from './styled';
import useUser from '@/hooks/useUser';
import PageAnchor from '@/components/PageAnchor';
import {
  COMPANY_DIVIDEND_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_INFORMATION_ANCHOR,
  COMPANY_MANAGEMENT_ANCHOR,
  COMPANY_OWNERSHIP_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_SUMMARY_ANCHOR,
  COMPANY_VALUE_ANCHOR,
  LegacyAnchor,
} from '@/constants/anchors';
import { intersectorThresholds } from '@/constants/intersectorThresholds';
import { Row, Column } from '@simplywallst/ui-core';
import LayoutContext from '../../components/Layout/context/LayoutContext/LayoutContext';
import { useSummaryV2 } from '@/hooks/useCompany';
import { useIntersectionListener } from '@/hooks/useIntersectionListener';
import { SEOHeader } from '@/pages/CompanyReport/components/SEOHeader/SEOHeader';
import { useSection } from './useSection';
import type { CompanyReportSection } from './useSection';
import OverviewSectionRaw from './components/OverviewSection';
import { LoadableChartDefinitionsProvider } from '@/components/ChartDefinitionsContext';
import { EventsModal } from '@/pages/CompanyReport/components/Overview/components/EventsModal';
import If from '@/components/If';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { useScrollLimit } from './hooks/useScrollLimit';
import { useCompanyLimits } from '@/hooks/useCompanyLimits';
import { useUserAccess } from '@/hooks/useUserAccess';
import { SideMenu } from '@/pages/CompanyReport/components/Overview/components/SideMenu';
import { useSelector } from 'react-redux';
import { getViewMode } from '@/redux/location/selectors';
import { ViewMode } from '@/pages/CompanyReport/';
import { getStaticUrl, useCloudflareImage } from '@/hooks/useCloudflareImage';
import SWSLogo from '@assets/images/logo/sws-logo.svg';
import { GeneratingReport } from '@/pages/CompanyReport/components/Overview/components/GeneratingReport';
import { useReportSplitHandler } from './components/ReportSplitHandlerProvider/ReportSplitHandlerProvider';
import { BrazeLogCompanyViewed } from '@/components/BrazeCustomEvents';
import { useCompanyShareLink } from '@/hooks/useCompanyShareToken';
import type { PageState } from '@/pages/CompanyReport/components/Overview/components/ViewLimitModal';
import { useRecentlyViewedCompany } from './hooks/useRecentlyViewedCompany';
import ReportSectionProvider from './components/ReportSectionProvider';
import DwellTrackingProvider from './components/DwellTrackingProvider';
import { MountWrapper } from './components/MountWrapper';
import { useVisibleSectionsContext } from './components/VisibleSectionsContextProvider';
import withLoading from './withLoading';
import { UpdateCompanyLimits } from './components/UpdateCompanyLimits';
import { useLocale } from '@/hooks/useLocale';
import { LocaleRedirect } from './components/LocaleRedirect';
import { isTranslationExperimental } from '@/utils/locale';
import { Footer } from '@/components/Footer';
import { cn } from '@simplywallst/ui-core/next';
import { FinancialData } from './components/Information/components/FinancialData';
import { Header } from '@/pages/CompanyReport/components/Overview/components/Header';
import DeprecatedHeader from '@/pages/CompanyReport/components/Overview/components/DeprecatedHeader';
import { Sentry } from '@/components/Sentry';
import { usePlatformDetect } from '@/hooks/usePlatformDetect';

const ValuationSectionRaw = loadable(
  () => import(/* webpackPrefetch: true */ './components/ValuationSection')
);

const InactiveSummary = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/pages/CompanyReport/components/Overview/components/InactiveSummary'
    )
);

const ShowFullReportConfirm = loadable(
  () => import(/* webpackPrefetch: true */ './components/ShowFullReportConfirm')
);

const AutoAddCompanyToWatchlist = loadable(
  () => import('@/components/AutoAddCompanyToWatchlist')
);

const PastContainerRaw = loadable(
  () =>
    import(/* webpackPrefetch: true */ '@/pages/CompanyReport/components/Past'),
  {
    resolveComponent: (component) => component.PastContainer,
  }
);
const FutureContainerRaw = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/pages/CompanyReport/components/Future'
    ),
  {
    resolveComponent: (component) => component.FutureContainer,
  }
);
const DividendContainerRaw = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/pages/CompanyReport/components/Dividend'
    ),
  {
    resolveComponent: (component) => component.DividendContainer,
  }
);
const ManagementContainerRaw = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/pages/CompanyReport/components/Management'
    ),
  {
    resolveComponent: (component) => component.ManagementContainer,
  }
);
const OwnershipContainerRaw = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/pages/CompanyReport/components/Ownership'
    ),
  {
    resolveComponent: (component) => component.OwnershipContainer,
  }
);
const HealthContainerRaw = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/pages/CompanyReport/components/Health'
    ),
  {
    resolveComponent: (component) => component.HealthContainer,
  }
);
const InformationContainerRaw = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/pages/CompanyReport/components/Information'
    ),
  {
    resolveComponent: (component) => component.InformationContainer,
  }
);

const I18nDisclaimer = loadable(() => import('./components/I18nDisclaimer'), {
  resolveComponent: (c) => c.I18nDisclaimer,
});

const OverviewSectionMemo = memo(OverviewSectionRaw);
const ValuationSectionMemo = memo(ValuationSectionRaw);

const OverviewSection = withLoading(OverviewSectionMemo);
const ValuationSection = withLoading(ValuationSectionMemo);
const PastContainer = withLoading(PastContainerRaw);
const FutureContainer = withLoading(FutureContainerRaw);
const DividendContainer = withLoading(DividendContainerRaw);
const ManagementContainer = withLoading(ManagementContainerRaw);
const OwnershipContainer = withLoading(OwnershipContainerRaw);
const HealthContainer = withLoading(HealthContainerRaw);
const InformationContainer = withLoading(InformationContainerRaw);

const GuestRegistrationModal = loadable(
  () => import('@/components/GuestRegistrationModal'),
  { ssr: false }
);

const NavigationMobile = loadable(
  () => import('./components/NavigationMobile')
);

const NavigationDesktop = loadable(
  () => import('./components/NavigationDesktop')
);

const ViewLimitModal = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Overview/components/ViewLimitModal'
    ),
  {
    resolveComponent: (component) => component.ViewLimitModal,
  }
);
const RewardedAdModal = loadable(
  () => import('@/pages/CompanyReport/components/RewardedAd'),
  {
    resolveComponent: (component) => component.RewardedAdModal,
  }
);

const PlansModal = loadable(
  () => import('@/components/OurPlans/components/PlansModal/PlansModal'),
  { ssr: false }
);
const FloatingMenu = loadable(() => import('./components/FloatingMenu'), {
  ssr: false,
  resolveComponent: (component) => component.FloatingMenu,
});

const SupportedLocaleDetector = loadable(
  () => import('./components/SupportedLocaleDetector'),
  { resolveComponent: (component) => component.SupportedLocaleDetector }
);

/**
 * This temporary hook is used to determine if the new header should be shown.
 * @todo: Remove this hook once the new header is fully rolled out
 */
const useHasNewHeader = () => {
  // Temporary disable the new header until we work out the registration issues
  return false;
};

function CompanyReportContents() {
  const layout = useContext(LayoutContext);
  const { data: user } = useUser();
  const { data: userAccess, status: userAccessStatus } = useUserAccess();
  const summary = useSummaryV2();
  const companyId =
    summary.status === 'success' ? summary.companyId : undefined;
  const uniqueSymbol =
    summary.status === 'success' ? summary.uniqueSymbol : undefined;
  const tickerSymbol =
    summary.status === 'success' ? summary.tickerSymbol : undefined;
  const limits = useCompanyLimits(companyId);
  const { currentSection, setCurrentSection } = useSection();

  const [openOverlay, setOpenOverlay] = useState(false);
  const [hasSeenRegistrationModal, setHasSeenRegistrationModal] =
    useState(false);

  const locale = useLocale();
  const experimental = isTranslationExperimental(locale);

  const hasNewHeader = useHasNewHeader();

  const [isRewardGranted, setIsRewardGranted] = useState(false);

  const onRewardGranted = useCallback(() => setIsRewardGranted(true), []);

  const { Track, trackEvent } = useTracking({
    type: 'track',
    companyId,
    uniqueSymbol,
    locale,
    hasNewHeader,
  });

  //This hook handles URL updates when changing sections
  const {
    onIntersectionPathUpdate,
    showPlansModal,
    showRegistration,
    setShowRegistration,
    setShowPlansModal,
  } = useReportSplitHandler();

  const {
    visibleSections,
    nextSectionLabel,
    previousSectionLabel,
    loadNextSection,
    loadPreviousSection,
    loadEverySection,
    hasNextSection,
    hasPreviousSection,
    loadSection,
    handleSectionOnMount,
    scrollToSection,
  } = useVisibleSectionsContext();

  useRecentlyViewedCompany({
    enabled: summary.status === 'success' && user.loggedIn,
    uniqueSymbol: summary.uniqueSymbol,
    canonicalUrl: summary.canonicalUrl,
  });

  const nextSectionAfterLoggedin = useRef<CompanyReportSection>();
  const sidebarNavigationRef = useRef<HTMLDivElement>(null);
  const handleNavigation = (section: CompanyReportSection) => {
    trackEvent({
      type: 'track',
      action: 'click',
      subject: 'loadSection',
      modifier: section,
    });

    if (!user.loggedIn) {
      setShowRegistration(true);
      nextSectionAfterLoggedin.current = section;
      return;
    }
    setCurrentSection(section);
    loadSection(section);
  };
  const redirectAfterLogin = useCallback(() => {
    // This setTimeout to prevent the redirect before closing the modal
    // that can lead to the race condition of changing section
    setTimeout(() => {
      if (nextSectionAfterLoggedin.current) {
        setCurrentSection(nextSectionAfterLoggedin.current);
        loadSection(nextSectionAfterLoggedin.current);
        nextSectionAfterLoggedin.current = undefined;
      }
    }, 0);
  }, [loadSection, setCurrentSection]);

  const showPreviousSection = () => {
    trackEvent({
      type: 'track',
      action: 'click',
      subject: 'loadPreviousSection',
      modifier: 'button',
    });
    loadPreviousSection();
    if (!user.loggedIn) {
      /**
       * delay scroll dispatch to give time to
       * recalculate the y position
       */
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('scroll'));
      }, 500);
    }
  };

  const showNextReport = () => {
    trackEvent({
      type: 'track',
      action: 'click',
      subject: 'loadNextSection',
      modifier: 'button',
    });
    loadNextSection();
  };

  const handleOnScroll = useCallback(
    (anchor = '') => {
      trackEvent({
        type: 'track',
        action: 'scroll',
        subject: 'loadNextSection',
        modifier: anchor,
      });
      loadNextSection();
    },
    [loadNextSection, trackEvent]
  );

  const isPremiumUser =
    userAccess.planType === 'premium' && user.billing.status !== 'trialing';

  const viewMode = useSelector(getViewMode);
  const { isSharedCompany, shareCompanyFetchStatus, token } =
    useCompanyShareLink();
  const shouldConsumeReportCredit =
    !isPremiumUser &&
    !isSharedCompany &&
    shareCompanyFetchStatus !== 'fetching';

  const { handleIntersectionChange } = useIntersectionListener({
    onIntersectionUpdate: (e, section) => {
      onIntersectionPathUpdate(e, token, section);
    },
  });
  const canShowRegistration = !user.loggedIn && showRegistration;

  const makeHandleIntersectionChange = useCallback(
    (anchor: string) => (e: IntersectionObserverEntry) => {
      handleIntersectionChange(e, anchor);
      if (
        e.isIntersecting &&
        e.intersectionRatio >= 0.5 &&
        !canShowRegistration &&
        e.boundingClientRect.top <
          0 /** one way to ensure the user is already scrolling is when this value is negative */
      ) {
        handleOnScroll(anchor);
      }
    },
    [handleIntersectionChange, canShowRegistration, handleOnScroll]
  );

  const limitExceededRef = useRef<boolean>();
  const onScrollLimitExceeded = useCallback(
    (limitExceeded, scrollY) => {
      if (!user.loggedIn && !isSharedCompany) {
        setShowRegistration(limitExceeded);
        //Prevent duplicate event calls
        if (limitExceededRef.current !== limitExceeded) {
          trackEvent({
            type: 'track',
            subject: 'scrollLimit',
            action: limitExceeded ? 'exceeded' : 'below',
            scrollY: scrollY,
          });
          limitExceededRef.current = limitExceeded;
        }
        return;
      }
    },
    [
      user.loggedIn,
      trackEvent,
      setShowRegistration,
      isSharedCompany,
      hasSeenRegistrationModal,
    ]
  );

  useEffect(() => {
    if (showRegistration) {
      trackEvent({
        type: 'track',
        subject: 'GuestRegistrationModal',
        modifier: 'scrollLimit',
        action: 'exceeded',
      });
    }
  }, [showRegistration, trackEvent]);

  const landedSection = useRef(currentSection);
  const initialTopRef = useRef<number | null>(null);
  const [sidebarPosition, setSidebarPosition] = useState<'fixed' | 'sticky'>(
    'fixed'
  );
  const { scrollLimitExceed } = useScrollLimit({
    initialSection: landedSection.current,
    onScrollLimitExceeded,
    enabled: true,
  });

  const sections = useMemo(
    () => ({
      summary: () => (
        <ReportSectionProvider section="summary">
          <Observer
            threshold={intersectorThresholds}
            onChange={makeHandleIntersectionChange(COMPANY_SUMMARY_ANCHOR)}
          >
            <div>
              <OverviewSection anchor="summary" />
            </div>
          </Observer>
        </ReportSectionProvider>
      ),
      valuation: () => (
        <ReportSectionProvider section="valuation">
          <Observer
            threshold={intersectorThresholds}
            onChange={makeHandleIntersectionChange(COMPANY_VALUE_ANCHOR)}
          >
            <div>
              <PageAnchor id={COMPANY_VALUE_ANCHOR} />
              <ValuationSection anchor="valuation" />
            </div>
          </Observer>
        </ReportSectionProvider>
      ),
      future: () => (
        <ReportSectionProvider section="future">
          <Observer
            threshold={intersectorThresholds}
            onChange={makeHandleIntersectionChange(COMPANY_FUTURE_ANCHOR)}
          >
            <SectionWrapper data-cy-id="report-section">
              <PageAnchor id={COMPANY_FUTURE_ANCHOR} />
              <FutureContainer sectionNumber={2} anchor="future" />
            </SectionWrapper>
          </Observer>
        </ReportSectionProvider>
      ),
      past: () => (
        <ReportSectionProvider section="past">
          <Observer
            threshold={intersectorThresholds}
            onChange={makeHandleIntersectionChange(COMPANY_PAST_ANCHOR)}
          >
            <SectionWrapper data-cy-id="report-section">
              <PageAnchor id={COMPANY_PAST_ANCHOR} />
              <PastContainer sectionNumber={3} anchor="past" />
            </SectionWrapper>
          </Observer>
        </ReportSectionProvider>
      ),
      health: () => (
        <ReportSectionProvider section="health">
          <Observer
            threshold={intersectorThresholds}
            onChange={makeHandleIntersectionChange(COMPANY_HEALTH_ANCHOR)}
          >
            <SectionWrapper data-cy-id="report-section">
              <PageAnchor id={COMPANY_HEALTH_ANCHOR} />
              <HealthContainer sectionNumber={4} anchor="health" />
            </SectionWrapper>
          </Observer>
        </ReportSectionProvider>
      ),
      dividend: () => (
        <ReportSectionProvider section="dividend">
          <Observer
            threshold={intersectorThresholds}
            onChange={makeHandleIntersectionChange(COMPANY_DIVIDEND_ANCHOR)}
          >
            <SectionWrapper data-cy-id="report-section">
              <PageAnchor id={COMPANY_DIVIDEND_ANCHOR} />
              <PageAnchor id={LegacyAnchor.INCOME} />
              <DividendContainer sectionNumber={5} anchor="dividend" />
            </SectionWrapper>
          </Observer>
        </ReportSectionProvider>
      ),
      management: () => (
        <ReportSectionProvider section="management">
          <Observer
            threshold={intersectorThresholds}
            onChange={makeHandleIntersectionChange(COMPANY_MANAGEMENT_ANCHOR)}
          >
            <SectionWrapper data-cy-id="report-section">
              <PageAnchor id={COMPANY_MANAGEMENT_ANCHOR} />
              <ManagementContainer sectionNumber={6} anchor="management" />
            </SectionWrapper>
          </Observer>
        </ReportSectionProvider>
      ),
      ownership: () => (
        <ReportSectionProvider section="ownership">
          <Observer
            threshold={intersectorThresholds}
            onChange={makeHandleIntersectionChange(COMPANY_OWNERSHIP_ANCHOR)}
          >
            <SectionWrapper data-cy-id="report-section">
              <PageAnchor id={COMPANY_OWNERSHIP_ANCHOR} />
              <OwnershipContainer sectionNumber={7} anchor="ownership" />
            </SectionWrapper>
          </Observer>
        </ReportSectionProvider>
      ),
      information: () => (
        <ReportSectionProvider section="information">
          <Observer
            threshold={intersectorThresholds}
            onChange={makeHandleIntersectionChange(COMPANY_INFORMATION_ANCHOR)}
          >
            <SectionWrapper data-cy-id="report-section">
              <PageAnchor id={COMPANY_INFORMATION_ANCHOR} />
              <InformationContainer sectionNumber={8} anchor="information" />
            </SectionWrapper>
          </Observer>
        </ReportSectionProvider>
      ),
    }),
    [makeHandleIntersectionChange]
  );

  const canShowLogo =
    userAccess.canHaveProfileImage &&
    typeof user.picture === 'string' &&
    user.picture !== '';
  const poweredByLogoSrc = useCloudflareImage({
    src: getStaticUrl('sws-powered-by-logo.png'),
  });

  useEffect(() => {
    if (
      viewMode === ViewMode.Print &&
      userAccess.canGetCompanyReportPdf &&
      userAccessStatus === 'success'
    ) {
      loadEverySection();
      setOpenOverlay(true);
      setTimeout(() => {
        setOpenOverlay(false);
      }, 6000);
      setTimeout(() => {
        window.print();
        setTimeout(() => {
          window.close();
        }, 500);
      }, 8000);
    } else {
      if (
        viewMode === ViewMode.Print &&
        !userAccess.canGetCompanyReportPdf &&
        userAccessStatus === 'success' &&
        summary.status === 'success'
      ) {
        window.location.replace(summary.canonicalUrl);
      }
    }
  }, [
    viewMode,
    userAccess.canGetCompanyReportPdf,
    userAccessStatus,
    summary.canonicalUrl,
    summary.status,
  ]);

  // Tracking
  const { status } = summary;
  const loggedPageLoad = useRef(false);
  useEffect(() => {
    if (
      !loggedPageLoad.current &&
      status === 'success' &&
      shareCompanyFetchStatus === 'idle' &&
      (!user.loggedIn || limits.status !== 'pending')
    ) {
      loggedPageLoad.current = true;
      trackEvent({
        type: 'track',
        subject: 'page',
        action: 'landed',
        companyId,
        uniqueSymbol,
        is_shared_company: isSharedCompany,
        views: limits.views,
        views_limit: limits.limit,
        is_rewarded:
          limits.rewards.findIndex(
            (company) => company.id === companyId?.toLowerCase()
          ) !== -1,
      });
    }
  }, [
    status,
    companyId,
    uniqueSymbol,
    isSharedCompany,
    shareCompanyFetchStatus,
    limits.views,
    limits.limit,
    trackEvent,
    user.loggedIn,
    limits.status,
    limits.rewards,
  ]);
  let pageState: PageState = 'incomplete';
  if (summary.status === 'success') {
    pageState = 'normal';
    if (
      limits.restricted &&
      !isSharedCompany &&
      shareCompanyFetchStatus !== 'fetching'
    ) {
      pageState = 'restricted';
    }
  }

  const experimentData = summary.tickerSymbol && {
    experimentData: {
      ticker: summary.tickerSymbol,
    },
  };

  const rewardedAdExpFlag = useFlag('mono-rewarded-ad-exp');
  const { device } = usePlatformDetect();

  const isFreeUser = userAccess.planType === 'free';
  const isReportRestricted = !limits.visited && pageState === 'restricted';
  const canAccessRewardedAdExp =
    RUNTIME_ENV === 'native' &&
    device === 'ios' &&
    isFreeUser &&
    isReportRestricted &&
    rewardedAdExpFlag.ready &&
    rewardedAdExpFlag.variant.name === 'variant';

  return (
    <DwellTrackingProvider>
      <Track>
        <>
          {user.loggedIn && summary.status === 'success' ? (
            <AutoAddCompanyToWatchlist
              user={user}
              companyId={summary.companyId}
            />
          ) : null}
          {summary.status === 'success' ? (
            <BrazeLogCompanyViewed companyId={summary.companyId} />
          ) : null}
          <SEOHeader section={currentSection} />
          <NavigationMobile
            section={currentSection}
            onSectionChange={handleNavigation}
          />
          <Container wide={layout.wider}>
            <If match={viewMode === ViewMode.Print}>
              {() =>
                userAccess.canDistributeCompanyReportPdf ? (
                  <PrintModeWatermark
                    canShowLogo={canShowLogo}
                    alignItem="center"
                  >
                    <Logo canShowLogo={canShowLogo}>
                      {typeof user.picture === 'string' ? (
                        <img src={user.picture} alt="logo" />
                      ) : null}
                    </Logo>
                    <img
                      src={poweredByLogoSrc}
                      alt="Powered by Simply Wall St"
                    />
                  </PrintModeWatermark>
                ) : (
                  <PrintModeWatermark
                    justifyContent="space-between"
                    alignItem="center"
                  >
                    <SWSLogo />
                    <WatermarkCopy>NOT FOR DISTRIBUTION</WatermarkCopy>
                  </PrintModeWatermark>
                )
              }
            </If>
            <FixedWrapper wide={layout.wider}>
              <If match={visibleSections.includes('summary')}>
                {() => (
                  <Row>
                    <Column>
                      <PageAnchor id={COMPANY_SUMMARY_ANCHOR} />
                      {hasNewHeader ? (
                        <Header />
                      ) : (
                        <DeprecatedHeader minHeight />
                      )}
                    </Column>
                  </Row>
                )}
              </If>

              <Row>
                <Column colLg={3} hiddenMdDown>
                  <div
                    data-testid="sidebar-container"
                    className={cn('top-x10', sidebarPosition)}
                    ref={sidebarNavigationRef}
                  >
                    <NavigationDesktop
                      section={currentSection}
                      onSectionChange={handleNavigation}
                      keepSnowflakePinned={!visibleSections.includes('summary')}
                    />
                  </div>
                </Column>
                <Column colLg={9}>
                  <CompanyReportWrapper
                    data-cy-id="company-report"
                    id="company-report"
                  >
                    {openOverlay && <GeneratingReport />}

                    <If match={hasPreviousSection}>
                      {() => (
                        <ShowFullReportConfirm
                          onClick={showPreviousSection}
                          position="top"
                        >
                          View {previousSectionLabel}
                        </ShowFullReportConfirm>
                      )}
                    </If>
                    {summary.status === 'success' &&
                      summary.classificationStatus !== 'active' && (
                        <InactiveSummary
                          companyId={summary.companyId}
                          uniqueSymbol={summary.uniqueSymbol}
                          classificationStatus={summary.classificationStatus}
                        />
                      )}
                    {experimental && user.loggedIn && <I18nDisclaimer />}
                    <LoadableChartDefinitionsProvider>
                      {visibleSections.map((section) => {
                        if (section in sections) {
                          return (
                            <MountWrapper
                              onMount={() => handleSectionOnMount(section)}
                              key={section}
                            >
                              {sections[section]()}
                            </MountWrapper>
                          );
                        }
                        return null;
                      })}
                    </LoadableChartDefinitionsProvider>
                    <If match={hasNextSection}>
                      {() => (
                        <ShowFullReportConfirm
                          onClick={showNextReport}
                          onIntersect={handleOnScroll}
                          position="bottom"
                        >
                          View {nextSectionLabel}
                        </ShowFullReportConfirm>
                      )}
                    </If>
                    <FinancialData name={summary.name || ''} />
                  </CompanyReportWrapper>
                  {!isSharedCompany &&
                    !showPlansModal &&
                    !isRewardGranted &&
                    (canAccessRewardedAdExp ? (
                      <Sentry
                        sentryCapture={{
                          team: 'wombats',
                        }}
                        globalDisabled
                      >
                        <RewardedAdModal
                          onClose={() =>
                            scrollToSection(landedSection.current, 0)
                          }
                          isOpen={
                            scrollLimitExceed && currentSection !== 'summary'
                          }
                          onRewardGranted={onRewardGranted}
                          company={summary}
                        />
                      </Sentry>
                    ) : (
                      <ViewLimitModal
                        //To avoid multiple modals doubling up dont show when theres a plan modal
                        scrollLimitExceed={!showPlansModal && scrollLimitExceed}
                        selected={currentSection}
                        landedSection={landedSection.current}
                        pageState={pageState}
                      />
                    ))}
                </Column>
              </Row>
              <Row>
                <Observer
                  threshold={intersectorThresholds}
                  onChange={(event) => {
                    if (event.isIntersecting) {
                      const boundingRect =
                        sidebarNavigationRef.current?.getBoundingClientRect();
                      if (typeof boundingRect !== 'undefined') {
                        const { height } = boundingRect;
                        const footerBoundingRect =
                          event.target.getBoundingClientRect();
                        if (initialTopRef.current === null) {
                          initialTopRef.current = footerBoundingRect.top;
                        }
                        if (
                          height + (initialTopRef.current || 0) >
                          footerBoundingRect.top
                        ) {
                          setSidebarPosition('sticky');
                        } else {
                          setSidebarPosition('fixed');
                        }
                      } else {
                        // some weird edge case where ref is not set
                        setSidebarPosition('fixed');
                      }
                    } else {
                      setSidebarPosition('fixed');
                    }
                  }}
                >
                  <div>
                    <Footer />
                  </div>
                </Observer>
              </Row>
            </FixedWrapper>
            <SideMenu />
          </Container>

          <If match={showPlansModal && !isRewardGranted}>
            {() =>
              canAccessRewardedAdExp ? (
                <Sentry
                  sentryCapture={{
                    team: 'wombats',
                  }}
                  globalDisabled
                >
                  <RewardedAdModal
                    onClose={() => scrollToSection(landedSection.current, 0)}
                    isOpen={scrollLimitExceed && currentSection !== 'summary'}
                    onRewardGranted={onRewardGranted}
                    company={summary}
                  />
                </Sentry>
              ) : (
                <PlansModal
                  limitHitterEventProperties={
                    tickerSymbol
                      ? {
                          trigger: 'company_report',
                          company_tickers: tickerSymbol,
                        }
                      : undefined
                  }
                  onClose={() => {
                    loadSection(landedSection.current);
                    setShowPlansModal(false);
                  }}
                >
                  {userAccess && (
                    <p data-cy-id="plan-restriction">{`${userAccess.viewCompanyReports} / ${userAccess.viewCompanyReports} reports viewed.`}</p>
                  )}
                  <h2>
                    {RUNTIME_ENV === 'native'
                      ? 'More stock analysis can be accessed via our other plans.'
                      : 'Upgrade to unlock your limits.'}
                  </h2>
                </PlansModal>
              )
            }
          </If>
          <If match={canShowRegistration}>
            {() => (
              <GuestRegistrationModal
                {...experimentData}
                canClose
                onClose={() => {
                  scrollToSection(landedSection.current, 0);
                  setShowRegistration(false);
                  //Track below action to avoid showing modal during inline-modal experiment
                  setHasSeenRegistrationModal(true);
                  trackEvent({
                    type: 'track',
                    subject: 'guestRegistrationModal',
                    modifier: 'dismissed',
                    action: 'click',
                  });
                  nextSectionAfterLoggedin.current = undefined;
                }}
                onRegisterSuccess={() => {
                  trackEvent({
                    type: 'track',
                    subject: 'guestRegistrationModal',
                    modifier: 'registration',
                    action: 'success',
                  });

                  redirectAfterLogin();
                }}
                onLoginSuccess={redirectAfterLogin}
              />
            )}
          </If>
          {!isSharedCompany && (
            <FloatingMenu companyId={companyId} tickerSymbol={tickerSymbol} />
          )}

          <EventsModal />
        </>
        {typeof companyId === 'string' && shouldConsumeReportCredit && (
          <UpdateCompanyLimits companyId={companyId} />
        )}
        {locale === 'en' && <SupportedLocaleDetector />}
        <LocaleRedirect />
      </Track>
    </DwellTrackingProvider>
  );
}

export default CompanyReportContents;
